import { z } from 'zod';
import { Run, RunStatus } from '@rossum/api-client/hooks';
import { alpha, SxProps, Theme } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { gridClasses } from '@rossum/ui/x-data-grid-pro';
import { parseAndValidate } from '../../../../utils/jsonParse';

export const getTransformedActivities = (activities: Run[]) =>
  activities.map(a => {
    // Not sure if this is a BE bug, but the job run timestamp may change.
    // UUID is unique for job runs, but not for hook runs.
    // This setup is safe, though it could be simplified if we confirm
    // that the job run timestamp never changes.
    const id =
      a.hookType === 'job'
        ? `${a.hookId}-${a.uuid}`
        : `${a.hookId}-${a.uuid}-${a.start}`;

    return {
      ...a,
      id,
    };
  });

export type TransformedActivity = ReturnType<
  typeof getTransformedActivities
>[number];

const getFormattedTimeDuration = (
  hours: number,
  minutes: number,
  seconds: number
) => {
  const hoursString = hours.toString().padStart(2, '0');
  const minutesString = minutes.toString().padStart(2, '0');
  const secondsString = seconds.toString().padStart(2, '0');
  return `${hoursString}:${minutesString}:${secondsString}`;
};

export const getActivityDuration = (
  startTimestamp: string,
  endTimestamp: string,
  lessThanOneSecondLabel: string
) => {
  const startDate = new Date(startTimestamp);
  const endDate = new Date(endTimestamp);

  const diff = endDate.getTime() - startDate.getTime();

  if (diff < 1000) return lessThanOneSecondLabel;

  const hours = Math.floor(diff / 1000 / 60 / 60);
  const minutes = Math.floor((diff / 1000 / 60) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return getFormattedTimeDuration(hours, minutes, seconds);
};

export const activitiesDataGridStyles: SxProps<Theme> = {
  backgroundColor: 'transparent',
  [`.${gridClasses.columnHeader}`]: {
    backgroundColor: t => t.palette.background.default,
  },
  [`.${gridClasses.row}`]: {
    borderBottom: 'none',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  [`.${gridClasses.row}.Mui-hovered`]: {
    backgroundColor: 'transparent',
  },
  [`.${gridClasses.detailPanel}`]: {
    backgroundColor: t => t.palette.background.paper,
    marginTop: '-1px',
    border: t => `1px solid ${alpha(t.palette.divider, 0.06)}`,
    borderTopColor: 'transparent',
    borderBottomLeftRadius: t => borderRadius.abs(t.shape.borderRadius, 'm'),
    borderBottomRightRadius: t => borderRadius.abs(t.shape.borderRadius, 'm'),
  },
  [`.${gridClasses.columnHeaders}, .${gridClasses.cell}`]: {
    border: 'none',
  },
  [`& .${gridClasses.columnHeaders} .${gridClasses.filler}`]: {
    backgroundColor: t => t.palette.background.default,
    border: 'none',
  },
  [`& .${gridClasses.cell}`]: {
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${gridClasses.columnHeader}`]: {
    borderBottom: 'none !important',
  },
  [`& .${gridClasses['columnHeader--pinnedRight']}`]: {
    border: 'none',
  },
  [`& .${gridClasses['cell--pinnedRight']}`]: {
    border: 'none',
    my: '1px',
    height: '42px',
  },
  [`& .${gridClasses['cell--pinnedRight']}:last-child`]: {
    borderTopRightRadius: t => borderRadius.abs(t.shape.borderRadius, 'm'),
    borderBottomRightRadius: t => borderRadius.abs(t.shape.borderRadius, 'm'),
  },
  [`& .${gridClasses['filler--pinnedRight']}`]: {
    background: 'transparent',
  },
};

export const isRunningJob = (status: RunStatus | undefined) => {
  return status === 'running' || status === 'waiting';
};

// Falls back to the previously cached cancelRequested so an optimistic
// "Cancelling…" state is not wiped while the backend asynchronously
// populates the timestamp on the run.
export const mergeRunUpdates = (existing: Run[], polled: Run[]): Run[] =>
  existing.map(existingRun => {
    const updatedRun = polled.find(r => r.uuid === existingRun.uuid);
    if (!updatedRun) return existingRun;
    return {
      ...updatedRun,
      cancelRequested:
        updatedRun.cancelRequested ?? existingRun.cancelRequested,
    };
  });

export const formatToJSONString = (value: Record<string, unknown>) =>
  JSON.stringify(value, null, 2);

export const resolveLogValue = (value: string | null) => {
  const plainValue = {
    value,
    editorProps: {
      mode: 'plain_text',
    },
  } as const;

  const parsedValue = parseAndValidate(
    value,
    z.record(z.unknown()).nullable(),
    null
  );

  return parsedValue === null
    ? plainValue
    : ({ value: formatToJSONString(parsedValue) } as const);
};
